/**
 * Web push for the signed-in person's own phone.
 *
 * Subscribing uses the service worker's PushManager with the server's VAPID public key
 * (from GET /api/config). On iPhone, push only exists inside an app added to the Home
 * Screen (iOS 16.4+), not in a Safari tab.
 */
import type { PushSubscriptionInput } from "../../shared/contracts";
import type { PublicConfig } from "../../shared/types";
import { del, get, post } from "./api";

export type PushUnavailableReason =
  | "no_key" // the server has no VAPID key configured
  | "ios_not_installed" // iPhone, but running in a Safari tab
  | "unsupported" // this browser has no web push
  | "no_service_worker"; // the app's service worker isn't running (e.g. in development)

export type PushAvailability = { ok: true; key: string } | { ok: false; reason: PushUnavailableReason };

export function isIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export function isAndroid(): boolean {
  return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
}

/** True when running as an installed (home-screen) app rather than a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

let configPromise: Promise<PublicConfig> | null = null;

export function getPublicConfig(): Promise<PublicConfig> {
  configPromise ??= get<PublicConfig>("/config").catch((e) => {
    configPromise = null;
    throw e;
  });
  return configPromise;
}

function browserSupportsPush(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Resolves the active service worker registration, or null if none appears quickly. */
async function registration(timeoutMs = 4000): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration();
  if (!existing) return null;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((r) => setTimeout(() => r(null), timeoutMs)),
  ]);
}

export async function pushAvailability(): Promise<PushAvailability> {
  if (!browserSupportsPush()) {
    return { ok: false, reason: isIos() && !isStandalone() ? "ios_not_installed" : "unsupported" };
  }
  const cfg = await getPublicConfig();
  if (!cfg.vapidPublicKey) return { ok: false, reason: "no_key" };
  if (!(await registration())) return { ok: false, reason: "no_service_worker" };
  return { ok: true, key: cfg.vapidPublicKey };
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await registration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

function base64UrlToBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const b64 = b64url
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(b64url.length / 4) * 4, "=");
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function toInput(sub: PushSubscription): PushSubscriptionInput {
  const json = sub.toJSON();
  return {
    endpoint: sub.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
  };
}

export class PushPermissionError extends Error {}

/**
 * Asks for permission (must be called from a tap), subscribes this phone and tells the
 * server. Re-subscribes if an old subscription used a different key.
 */
export async function enablePush(vapidPublicKey: string): Promise<void> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new PushPermissionError("Notifications weren't allowed on this phone.");
  }
  const reg = await registration();
  if (!reg) throw new Error("The app isn't fully installed yet. Close it, open it again and retry.");
  const key = base64UrlToBytes(vapidPublicKey);
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    const existingKey = sub.options.applicationServerKey;
    const same =
      existingKey &&
      new Uint8Array(existingKey).length === key.length &&
      new Uint8Array(existingKey).every((b, i) => b === key[i]);
    if (!same) {
      await sub.unsubscribe();
      sub = null;
    }
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  await post("/me/push-subscriptions", toInput(sub));
}

/** Unsubscribes this phone and tells the server to forget it. */
export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  // Tell the server first: if that fails, the phone stays subscribed and they can retry.
  await del("/me/push-subscriptions", { endpoint: sub.endpoint });
  await sub.unsubscribe();
}

/**
 * On sign-out: stop this phone getting the person's notifications (shift reminders,
 * approval requests naming cleaners and sites) once someone else may be holding it.
 * Tells the server to forget the subscription while the session still exists, then
 * unsubscribes the phone even if that failed (with no signal, say): the server drops a
 * dead subscription the next time it tries to use it. Never throws; gives up after a
 * few seconds so signing out isn't held up.
 */
export async function forgetPushOnSignOut(timeoutMs = 5000): Promise<void> {
  const work = (async () => {
    if (!browserSupportsPush()) return;
    const sub = await currentSubscription();
    if (!sub) return;
    try {
      await del("/me/push-subscriptions", { endpoint: sub.endpoint });
    } finally {
      await sub.unsubscribe().catch(() => false);
    }
  })().catch(() => {});
  await Promise.race([work, new Promise<void>((r) => setTimeout(r, timeoutMs))]);
}

/**
 * If this phone already has a push subscription, (re)registers it for whoever is signed
 * in now. The server moves an endpoint to the person who registers it, so notifications
 * follow the person using the phone. Never throws.
 */
export async function syncPushSubscription(): Promise<void> {
  try {
    if (!browserSupportsPush() || Notification.permission !== "granted") return;
    const sub = await currentSubscription();
    if (sub) await post("/me/push-subscriptions", toInput(sub));
  } catch {
    // Best effort: the Settings switch still works.
  }
}

export function sendTestNotification(): Promise<{ sent: number }> {
  return post<{ sent: number }>("/me/push-test");
}
